import { useCallback, useState } from "react";
import dayjs from "dayjs";
import FileSaver from "file-saver";
import { toast } from "react-toastify";
import {
  generateZip,
  reportUnexpectedError,
  SmartApplyApiError,
} from "@graffer-inc/smart-apply";
import apiClient from "../../../../../../application/apis/api-client";

type exportSettingRowType = {
  originProcedureId: string;
  originProcedureName: string;
  destManageGroupId: string;
  destManageGroupName: string;
};

enum csvColumnIndex {
  originProcedureId = 0,
  originProcedureName = 1,
  destManageGroupId = 2,
  destManageGroupName = 3,
}

export const useServiceProviderSmartApplyProceduresBulkExportByCSVHooks =
  () => {
    const [isBulkExporting, setIsBulkExporting] = useState(false);
    const [errMsg, setErrMsg] = useState<string | undefined>();

    const [csvFile, setCSVFile] = useState<File | undefined>();
    const [exportSettingRows, setExportSettingRows] = useState<
      exportSettingRowType[]
    >([]);

    const exportable = !!csvFile && exportSettingRows.length > 0;
    const exportableLimit = 50;

    const csvHeaderLabels = {
      originProcedureId: "移行元手続きID",
      originProcedureName: "移行元手続き名",
      destManageGroupId: "移行先管理グループID",
      destManageGroupName: "移行先管理グループ名",
    };

    const colSplitter = ",";

    const validateHeader = (header: string): boolean => {
      const cols = header.split(colSplitter);

      if (
        cols.length !== 4 ||
        !(
          cols[csvColumnIndex.originProcedureId] ===
            csvHeaderLabels.originProcedureId &&
          cols[csvColumnIndex.originProcedureName] ===
            csvHeaderLabels.originProcedureName &&
          cols[csvColumnIndex.destManageGroupId] ===
            csvHeaderLabels.destManageGroupId &&
          cols[csvColumnIndex.destManageGroupName] ===
            csvHeaderLabels.destManageGroupName
        )
      ) {
        setErrMsg(
          `csvファイルは、${csvHeaderLabels.originProcedureId}、${csvHeaderLabels.originProcedureName}、${csvHeaderLabels.destManageGroupId}、${csvHeaderLabels.destManageGroupName} の4列である必要があります。`,
        );
        return false;
      }

      return true;
    };

    const validateBody = (body: string[]): boolean => {
      const onlyNumStringRegexp = /^[0-9]{19}$/;

      if (body.length === 0) {
        setErrMsg("エクスポート対象のデータがありません。");
        return false;
      }

      if (body.length > exportableLimit) {
        setErrMsg(
          `エクスポート対象のデータは最大${exportableLimit}件までです。`,
        );
        return false;
      }

      if (
        !body.every((r) => {
          const cols = r.split(colSplitter);
          return (
            cols.length === 4 &&
            onlyNumStringRegexp.test(cols[0]) &&
            onlyNumStringRegexp.test(cols[2])
          );
        })
      ) {
        setErrMsg(
          "csvファイルは4列かつID列は19桁の半角数値である必要があります。",
        );
        return false;
      }

      return true;
    };

    const handleSetFile = useCallback(async (csv: File | undefined) => {
      if (!csv) return;
      const text = await readFile(csv);
      const textRows = text.split(/\r\n|\r|\n/);

      const headerTextRow = textRows[0];
      // 自治体からの移行シートはexcel形式で受領する
      // excelからcsvに変換した場合に、末尾に空文字の行が入ってしまうためここでfilterをかけておく
      const bodyTextRows = textRows.slice(1).filter((r) => r !== "");

      if (!validateHeader(headerTextRow) || !validateBody(bodyTextRows)) {
        setCSVFile(csv);
        setExportSettingRows([]);
        return;
      }

      const rows: exportSettingRowType[] = bodyTextRows.map((r) => {
        const cols = r.split(colSplitter);
        return {
          originProcedureId: cols[csvColumnIndex.originProcedureId],
          originProcedureName: cols[csvColumnIndex.originProcedureName],
          destManageGroupId: cols[csvColumnIndex.destManageGroupId],
          destManageGroupName: cols[csvColumnIndex.destManageGroupName],
        };
      });

      setExportSettingRows(rows);
      setCSVFile(csv);
    }, []);

    const readFile = (file: Blob): Promise<string> => {
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error);
        reader.onload = () => resolve((reader.result as string) || "");
        reader.readAsText(file);
      });
    };

    const handleDeleteFile = useCallback(() => {
      setErrMsg(undefined);
      setCSVFile(undefined);
      setExportSettingRows([]);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleBulkExport = async () => {
      setIsBulkExporting(true);

      try {
        const promises = exportSettingRows.map(async (r) => {
          const { data } =
            await apiClient.exportProcedureSettingsWithDestManageGroupId(
              r.originProcedureId,
              r.destManageGroupId,
            );
          const blob = new Blob([JSON.stringify(data)], {
            type: "application/json",
          });
          return {
            name: `${r.originProcedureId}-${r.originProcedureName}.json`,
            blob: blob as Blob,
          };
        });
        const files = await Promise.all(promises);
        const zip = await generateZip(files);
        FileSaver.saveAs(zip, `export-procedures-${dayjs().unix()}.zip`);
        toast.success("一括エクスポートが完了しました。");
      } catch (e) {
        // NOTE
        // 必要であれば exportProcedureSettingsWithDestManageGroupId をtry catchで囲み、手続きIDを抽出してフィードバックする
        if (SmartApplyApiError.is404(e)) {
          setErrMsg(
            "存在しない移行元手続きIDがエクスポート対象に設定されています。",
          );
        }
        toast.error(
          "一括エクスポート時にエラーが発生しました。時間をおいて再度実行してください。",
        );
        reportUnexpectedError(e);
      } finally {
        setIsBulkExporting(false);
      }
    };

    return {
      isBulkExporting,
      errMsg,
      exportable,
      exportableLimit,
      csvHeaderLabels,
      csvFile,
      exportSettingRows,
      handleSetFile,
      handleDeleteFile,
      handleBulkExport,
    };
  };
